import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types/api';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  is_active: boolean;
  is_verified: boolean;
  tenant_id?: string;
  tenant_name?: string;
  last_login_at?: string;
  created_at: string;
  updated_at?: string;
  failed_login_attempts?: number;
  locked_until?: string;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  tenant_id?: string;
  roles?: string[];
  is_active?: boolean;
  is_verified?: boolean;
}

export interface UpdateUserRequest {
  email?: string;
  password?: string;
  first_name?: string;
  last_name?: string;
  tenant_id?: string;
  roles?: string[];
  is_active?: boolean;
  is_verified?: boolean;
}

/**
 * User API endpoints
 */
export const userApi = {
  /**
   * Get all users (admin only) with pagination and filters
   */
  getUsers: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    tenant_id?: string;
    role?: string;
    is_active?: boolean;
  }): Promise<{
    users: User[];
    total: number;
    pages: number;
    current_page: number;
    per_page: number;
  }> => {
    const response = await apiClient.get<
      ApiResponse<{
        users: User[];
        total: number;
        pages: number;
        current_page: number;
        per_page: number;
      }>
    >('/users/admin/users', {
      params,
    });
    return response.data.data!;
  },

  /**
   * Get single user by ID (admin only)
   */
  getUser: async (id: string): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>(
      `/users/admin/users/${id}`
    );
    return response.data.data!;
  },

  /**
   * Create new user (admin only)
   */
  createUser: async (data: CreateUserRequest): Promise<User> => {
    const response = await apiClient.post<ApiResponse<User>>(
      '/users/admin/users',
      data
    );
    return response.data.data!;
  },

  /**
   * Update existing user (admin only)
   */
  updateUser: async (id: string, data: UpdateUserRequest): Promise<User> => {
    const response = await apiClient.patch<ApiResponse<User>>(
      `/users/admin/users/${id}`,
      data
    );
    return response.data.data!;
  },

  /**
   * Delete user (admin only)
   */
  deleteUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/admin/users/${id}`);
  },

  /**
   * Unlock user account (admin only)
   */
  unlockUser: async (id: string): Promise<void> => {
    await apiClient.post(`/users/admin/users/${id}/unlock`);
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>('/users/me');
    return response.data.data!;
  },

  /**
   * Update current user profile
   */
  updateCurrentUser: async (data: {
    first_name?: string;
    last_name?: string;
    current_password?: string;
    new_password?: string;
  }): Promise<void> => {
    await apiClient.patch('/users/me', data);
  },
};
